import {
  Box,
  Card,
  Grid2,
  Button,
  Typography,
  Table,
  TableHead,
  Avatar,
  TableRow,
  TableCell,
  TableBody,
  CardMedia,
  TextField,
  Chip,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import { useApi } from "../../utils/api";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useUserInfo } from "../../utils/user";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/CloseOutlined";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { createSpeechServicesPonyfill } from "web-speech-cognitive-services";

import { EXERCISE_TYPES } from "../AddWorkoutDetails";
import { Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import arrayMutators from "final-form-arrays";
import AddExercise from "./AddExercise";
import WorkoutMetaFields from "./WorkoutMetaFields";
import VideoSelectionModal from "./VideoSelectionModal";

const SUBSCRIPTION_KEY = import.meta.env.VITE_AZURE_SPEECH_SUBSCRIPTION_KEY;
const REGION = import.meta.env.VITE_AZURE_SPEECH_REGION;

const { SpeechRecognition: AzureSpeechRecognition } =
  createSpeechServicesPonyfill({
    credentials: {
      region: REGION,
      subscriptionKey: SUBSCRIPTION_KEY,
    },
  });
SpeechRecognition.applyPolyfill(AzureSpeechRecognition);

const ReviewClientActivity = ({
  reviewingActivity,
  onReviewed,
  activityLoading,
  hideReviewButton = false,
  onWorkoutReviewed,
}) => {
  const { saveWorkoutDetails, addWorkoutForUser } = useApi();
  const [reviewed, setReviewed] = useState(reviewingActivity?.reviewed);
  const [loading, setLoading] = useState(false);
  const [autoReviewLoading, setAutoReviewLoading] = useState(false);
  const [activity, setActivity] = useState(reviewingActivity);
  const [newWorkoutData, setNewWorkoutData] = useState(null);
  const [showAutoReviewedWorkout, setShowAutoReviewedWorkout] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [editingExerciseIndex, setEditingExerciseIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setReviewed(reviewingActivity?.reviewed);
    setActivity(reviewingActivity);
    setNewWorkoutData(null);
    setShowAutoReviewedWorkout(false);
    setEditingExerciseIndex(null);
    handleAutoReview();
  }, [reviewingActivity]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getDefaultExerciseType = (index) => {
    if (index === 0) return EXERCISE_TYPES.WARMUP;
    if (index === 1) return EXERCISE_TYPES.MAIN;
    return EXERCISE_TYPES.ACCESSORY;
  };

  const normalizeSets = useCallback(
    (sets = []) =>
      sets.map((set) => ({
        reps: set.reps ?? "",
        weight: set.weight ?? "",
        rpe: set.rpe ?? "",
        time: set.time ?? "",
        distance: set.distance ?? "",
        degree: set.degree ?? "",
        video: set.video || "",
        weightPerSide: set.weightPerSide ?? false,
        repsPerSide: set.repsPerSide ?? false,
        backoffPercent: set.backoffPercent ?? "",
        backoffFromIndex:
          typeof set.backoffFromIndex === "number"
            ? set.backoffFromIndex
            : null,
      })),
    []
  );

  const getExerciseFields = useCallback((exercise) => {
    const hasBackoffSet = (sets) =>
      Array.isArray(sets) &&
      sets.some(
        (s) =>
          s &&
          s.backoffPercent !== undefined &&
          s.backoffPercent !== null &&
          s.backoffFromIndex !== undefined &&
          s.backoffFromIndex !== null
      );
    const baseFromFields = (fields) => {
      if (!Array.isArray(fields)) return ["REPS", "RPE", "WEIGHT"];
      const hasBackoff = fields.some((f) => f && f.toUpperCase() === "BACKOFF");
      let primary = fields
        .filter((f) => f && f.toUpperCase() !== "BACKOFF")
        .slice(0, 3);
      // Legacy compatibility: old workouts often stored only RPE/WEIGHT,
      // but reps were still required. Auto-add REPS for strength-style fields.
      const hasStrengthField =
        primary.includes("RPE") || primary.includes("WEIGHT");
      const isPureTimedOrMeasured =
        primary.includes("TIME") ||
        primary.includes("DISTANCE") ||
        primary.includes("DEGREE");
      if (hasStrengthField && !isPureTimedOrMeasured && !primary.includes("REPS")) {
        primary = ["REPS", ...primary].slice(0, 3);
      }
      return hasBackoff ? [...primary, "BACKOFF"] : primary;
    };
    if (Array.isArray(exercise?.fields)) {
      const base = baseFromFields(exercise.fields);
      const hasBackoff =
        hasBackoffSet(exercise?.sets) ||
        (exercise?.circuit &&
          exercise.circuit.some((round) =>
            Array.isArray(round) &&
            round.some((ex) => hasBackoffSet(ex?.sets))
          ));
      return hasBackoff && !base.includes("BACKOFF")
        ? [...base, "BACKOFF"]
        : base;
    }
    const firstSet = exercise?.sets?.[0];
    if (firstSet && (firstSet.type != null || (Array.isArray(firstSet.types) && firstSet.types.length > 0))) {
      const base =
        Array.isArray(firstSet.types) && firstSet.types.length > 0
          ? firstSet.types.slice(0, 3)
          : firstSet.type === "TIME"
            ? ["TIME"]
            : firstSet.type === "DEGREE"
              ? ["DEGREE"]
              : firstSet.type === "RPE"
                ? ["REPS", "RPE", "WEIGHT"]
                : ["REPS", "WEIGHT", "RPE"];
      const hasBackoff = hasBackoffSet(exercise?.sets);
      return hasBackoff && !base.includes("BACKOFF") ? [...base, "BACKOFF"] : base;
    }
    const hasBackoff = hasBackoffSet(exercise?.sets);
    return hasBackoff
      ? ["REPS", "RPE", "WEIGHT", "BACKOFF"]
      : ["REPS", "RPE", "WEIGHT"];
  }, []);

  const nextWeekInitialValues = useMemo(() => {
    if (!newWorkoutData) return null;
    return {
      name: newWorkoutData?.name || "",
      description: newWorkoutData?.description || "",
      currentWeek: newWorkoutData?.currentWeek || 1,
      programLength: newWorkoutData?.programLength || 12,
      completed: false,
      exercises:
        newWorkoutData?.exercises?.map((exercise, index) => ({
          ...exercise,
          weightUnit: exercise.weightUnit || "lbs",
          exerciseType: exercise.exerciseType || getDefaultExerciseType(index),
          fields: getExerciseFields(exercise),
          rounds:
            exercise.exerciseType === "circuit" ? exercise.rounds : undefined,
          circuit:
            exercise.exerciseType === "circuit"
              ? exercise.circuit && Array.isArray(exercise.circuit)
                ? exercise.circuit.map((round) =>
                    round.map((ex) => ({
                      ...ex,
                      fields: getExerciseFields(ex),
                      sets: normalizeSets(ex.sets),
                    }))
                  )
                : exercise.subExercises && exercise.subExercises.length > 0
                ? Array(exercise.rounds || 1)
                    .fill(null)
                    .map(() =>
                      exercise.subExercises.map((sub) => ({
                        ...sub,
                        fields: getExerciseFields(sub),
                        sets: normalizeSets(sub.sets),
                      }))
                    )
                : []
              : undefined,
          subExercises:
            exercise.exerciseType === "circuit"
              ? (exercise.subExercises || []).map((sub) => ({
                  ...sub,
                  sets: normalizeSets(sub.sets),
                }))
              : [],
          sets:
            exercise.exerciseType === "circuit"
              ? []
              : normalizeSets(exercise.sets),
        })) || [],
      date: newWorkoutData?.date || moment().add(1, "week"),
    };
  }, [newWorkoutData, normalizeSets, getExerciseFields]);

  const toInt = (v, fallback) =>
    v === "" || v === undefined || v === null ? fallback : parseInt(v, 10);
  const toFloat = (v) =>
    v === "" || v === undefined || v === null ? null : parseFloat(v);

  const prepareNextWeekPayload = (values) => ({
    ...values,
    currentWeek: toInt(values.currentWeek, 1),
    programLength: toInt(values.programLength, 12),
    completed: values.completed || false,
    exercises:
      values.exercises?.map((e, idx) => ({
        name: e.name,
        description: e.description,
        video: e.video,
        restTime: toInt(e.restTime, null),
        weightUnit: e.weightUnit || "lbs",
        exerciseType: e.exerciseType || getDefaultExerciseType(idx),
        rounds:
          e.exerciseType === "circuit"
            ? e.rounds
              ? parseInt(e.rounds)
              : e.sets?.length
              ? parseInt(e.sets.length)
              : 1
            : undefined,
        comments: e.comments || [],
        distanceUnit: (Array.isArray(e.fields) && e.fields.includes("DISTANCE")) ? (e.distanceUnit || "m") : undefined,
        subExercises:
          e.exerciseType === "circuit"
            ? (e.subExercises || []).map((sub) => ({
                name: sub.name,
                description: sub.description,
                video: sub.video,
                fields: getExerciseFields(sub),
                distanceUnit: (Array.isArray(sub.fields) && sub.fields.includes("DISTANCE")) ? (sub.distanceUnit || "m") : undefined,
                sets: (sub.sets || []).map((s) => {
                  const backoffPercent =
                    s.backoffPercent === "" || s.backoffPercent == null ? null : parseFloat(s.backoffPercent);
                  const backoffFromIndex =
                    s.backoffFromIndex === "" || s.backoffFromIndex == null ? null : parseInt(s.backoffFromIndex, 10);
                  return {
                    reps: toInt(s.reps, null),
                    weight: toFloat(s.weight),
                    rpe: toInt(s.rpe, null),
                    time: toInt(s.time, null),
                    distance: s.distance != null && s.distance !== "" ? toFloat(s.distance) : null,
                    degree: s.degree != null && s.degree !== "" ? toFloat(s.degree) : null,
                    video: s.video || undefined,
                    weightPerSide: s.weightPerSide ?? false,
                    repsPerSide: s.repsPerSide ?? false,
                    backoffPercent,
                    backoffFromIndex,
                  };
                }),
              }))
            : [],
        circuit:
          e.exerciseType === "circuit" && e.circuit && Array.isArray(e.circuit)
            ? e.circuit.map((round) =>
                round.map((ex) => {
                  const mapSet = (s) => {
                    const backoffPercent =
                      s.backoffPercent === "" || s.backoffPercent == null ? null : parseFloat(s.backoffPercent);
                    const backoffFromIndex =
                      s.backoffFromIndex === "" || s.backoffFromIndex == null ? null : parseInt(s.backoffFromIndex, 10);
                    return {
                      reps: toInt(s.reps, null),
                      weight: toFloat(s.weight),
                      rpe: toInt(s.rpe, null),
                      time: toInt(s.time, null),
                      distance: s.distance != null && s.distance !== "" ? toFloat(s.distance) : null,
                      degree: s.degree != null && s.degree !== "" ? toFloat(s.degree) : null,
                      video: s.video || undefined,
                      weightPerSide: s.weightPerSide ?? false,
                      repsPerSide: s.repsPerSide ?? false,
                      backoffPercent,
                      backoffFromIndex,
                    };
                  };
                  return {
                    name: ex.name,
                    description: ex.description,
                    video: ex.video,
                    fields: getExerciseFields(ex),
                    distanceUnit: (Array.isArray(ex.fields) && ex.fields.includes("DISTANCE")) ? (ex.distanceUnit || "m") : undefined,
                    sets: (ex.sets || []).map(mapSet),
                  };
                })
              )
            : undefined,
        fields: getExerciseFields(e),
        sets:
          e.exerciseType === "circuit"
            ? []
            : (e.sets || []).map((s) => {
                const backoffPercent =
                  s.backoffPercent === "" || s.backoffPercent == null ? null : parseFloat(s.backoffPercent);
                const backoffFromIndex =
                  s.backoffFromIndex === "" || s.backoffFromIndex == null ? null : parseInt(s.backoffFromIndex, 10);
                return {
                  reps: toInt(s.reps, null),
                  weight: toFloat(s.weight),
                  rpe: toInt(s.rpe, null),
                  time: toInt(s.time, null),
                  distance: s.distance != null && s.distance !== "" ? toFloat(s.distance) : null,
                  degree: s.degree != null && s.degree !== "" ? toFloat(s.degree) : null,
                  video: s.video || undefined,
                  weightPerSide: s.weightPerSide ?? false,
                  repsPerSide: s.repsPerSide ?? false,
                  backoffPercent,
                  backoffFromIndex,
                };
              }),
      })) || [],
    date: moment.isMoment(values.date)
      ? values.date.unix()
      : moment(values.date).unix(),
  });

  const handleOpenVideoModal = (index) => {
    setEditingExerciseIndex(index);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setEditingExerciseIndex(null);
  };

  const handleSelectVideo = (form, videoUrl) => {
    if (editingExerciseIndex !== null) {
      form.change(`exercises[${editingExerciseIndex}].video`, videoUrl);
      handleCloseVideoModal();
    }
  };

  const toggleReviewed = async () => {
    setLoading(true);
    await saveWorkoutDetails(reviewingActivity.id, { reviewed: !reviewed });
    setReviewed(!reviewed);
    setLoading(false);
    onReviewed();
  };

  const handleShowAutoReview = async () => {
    // If we already have prepared data, simply reveal the UI without re-running
    if (newWorkoutData) {
      setShowAutoReviewedWorkout(true);
      return;
    }
  };

  const handleAutoReview = async () => {
    setAutoReviewLoading(true);
    try {
      // Validate that we have the necessary data
      if (
        !reviewingActivity ||
        !reviewingActivity.exercises ||
        reviewingActivity.exercises.length === 0
      ) {
        showSnackbar("Invalid workout data for auto-review.", "error");
        return;
      }

      // Always copy the workout to next week (remove shouldCopy logic)
      // Create new workout for next week with adjusted weights
      const nextWeekDate = moment.unix(reviewingActivity.date).add(1, "week");

      console.log("Original workout data:", reviewingActivity);
      console.log("Original exercises:", reviewingActivity.exercises);

      // Create a deep copy of the exercises to avoid modifying the original workout
      const originalExercises = JSON.parse(
        JSON.stringify(reviewingActivity.exercises)
      );
      console.log("Deep copied exercises:", originalExercises);

      // Calculate the next week for the new workout
      // Ensure currentWeek and programLength are numbers, defaulting if missing
      const currentWeek = reviewingActivity.currentWeek
        ? parseInt(reviewingActivity.currentWeek, 10)
        : 1;
      const programLength = reviewingActivity.programLength
        ? parseInt(reviewingActivity.programLength, 10)
        : 12;

      let nextWeek = currentWeek;
      console.log(
        `Current week: ${currentWeek}, Program length: ${programLength}`
      );
      if (currentWeek < programLength) {
        nextWeek = currentWeek + 1;
        console.log(`Incrementing week to: ${nextWeek}`);
      } else {
        console.log(
          `Not incrementing week (current week ${currentWeek} >= program length ${programLength})`
        );
      }

      // Helper function to calculate weight adjustment based on avgRPE
      const calculateWeightAdjustment = (
        baseWeight,
        avgRPE,
        weightUnit,
        isAccessory
      ) => {
        if (baseWeight === 0) {
          console.log(`      Base weight is 0, keeping weight at 0`);
          return 0;
        }

        let increment = 0;
        if (avgRPE >= 6 && avgRPE < 7) {
          increment = weightUnit === "kg" ? (isAccessory ? 2.5 : 5) : 5;
        } else if (avgRPE >= 7 && avgRPE < 9) {
          increment = weightUnit === "kg" ? (isAccessory ? 1.25 : 2.5) : 5;
        } else if (avgRPE >= 9 && avgRPE < 10) {
          increment = 0; // Keep same weight
        } else if (avgRPE >= 10) {
          // Reduce by 10% and round appropriately
          if (weightUnit === "kg") {
            return Math.round(baseWeight * 0.9 * 2) / 2;
          } else {
            const reducedWeight = baseWeight * 0.9;
            if (reducedWeight < 50) {
              return Math.round(reducedWeight / 5) * 5;
            } else {
              return Math.round(reducedWeight / 10) * 10;
            }
          }
        }

        return baseWeight + increment;
      };

      // Helper function to process sets for an exercise or subExercise
      const processSets = (sets, weightUnit, exerciseType, fields) => {
        const hasTimeOrDistance =
          Array.isArray(fields) &&
          (fields.includes("TIME") || fields.includes("DISTANCE"));

        // For time/distance-based sets, just carry over existing values
        // (time/distance/reps/etc.) and clear video/completed/RPE, without
        // applying any weight/RPE auto-adjust logic.
        if (hasTimeOrDistance) {
          return (sets || []).map((set) => ({
            ...set,
            video: null,
            completed: false,
          }));
        }

        // Group sets by reps count
        const setsByReps = {};
        (sets || []).forEach((set, index) => {
          const reps = set.reps;
          if (!setsByReps[reps]) {
            setsByReps[reps] = [];
          }
          setsByReps[reps].push({ ...set, originalIndex: index });
        });

        // Process each rep group
        const repGroups = Object.keys(setsByReps).map((reps) => {
          const sets = setsByReps[reps];
          const repsNum = parseInt(reps);

          // Calculate average RPE for this rep group
          const rpeValues = sets
            .map((set) => set.rpe)
            .filter((rpe) => rpe && !isNaN(rpe));

          console.log(`  Rep group ${reps}: RPE values found:`, rpeValues);

          if (rpeValues.length === 0) {
            console.log(
              `  Rep group ${reps}: No RPE values, keeping sets as-is`
            );
            return {
              reps: repsNum,
              sets,
              avgRPE: null,
              standardizedWeight: null,
            };
          }

          const avgRPE =
            rpeValues.reduce((sum, rpe) => sum + rpe, 0) / rpeValues.length;
          console.log(`  Rep group ${reps}: Average RPE: ${avgRPE}`);

          // Find standardized weight for this rep group
          const weightValues = sets
            .map((set) => set.weight)
            .filter(
              (weight) =>
                weight !== undefined && weight !== null && !isNaN(weight)
            )
            .map((weight) => Number(weight));

          let standardizedWeight = null;
          if (weightValues.length > 0) {
            const avgWeight =
              weightValues.reduce((sum, weight) => sum + weight, 0) /
              weightValues.length;
            console.log(`  Rep group ${reps}: Average weight: ${avgWeight}`);

            // Find the actual weight from a set that is closest to the average
            const closestWeight = weightValues.reduce((closest, current) => {
              return Math.abs(current - avgWeight) <
                Math.abs(closest - avgWeight)
                ? current
                : closest;
            });

            standardizedWeight = closestWeight;
            console.log(
              `  Rep group ${reps}: Standardized weight: ${standardizedWeight}`
            );
          }

          // If the user adjusts weight mid-exercise, we want next week to
          // start from the last weight they ended with.
          // "down" => last < first, "up" => last > first
          const numericWeightsInOrder = sets
            .map((set) => {
              if (
                set.weight === undefined ||
                set.weight === null ||
                isNaN(set.weight)
              ) {
                return null;
              }
              const n = Number(set.weight);
              return isNaN(n) ? null : n;
            })
            .filter((n) => n !== null);

          let trendWeight = null;
          if (numericWeightsInOrder.length > 1) {
            const firstW = numericWeightsInOrder[0];
            const lastW = numericWeightsInOrder[numericWeightsInOrder.length - 1];
            if (lastW < firstW || lastW > firstW) {
              trendWeight = lastW;
            }
          }

          return { reps: repsNum, sets, avgRPE, standardizedWeight, trendWeight };
        });

        // Process each rep group and apply adjustments
        const adjustedSets = new Array(sets.length);
        repGroups.forEach((group) => {
          const { reps, sets: groupSets, avgRPE, standardizedWeight, trendWeight } = group;
          const isAccessory = exerciseType === EXERCISE_TYPES.ACCESSORY;

          groupSets.forEach((set) => {
            console.log(`Processing set:`, set);

            // Ensure weight and rpe are numbers if present
            const weight =
              set.weight !== undefined && set.weight !== null
                ? Number(set.weight)
                : null;
            const rpe =
              set.rpe !== undefined && set.rpe !== null
                ? Number(set.rpe)
                : null;

            console.log(`Converted weight: ${weight}, rpe: ${rpe}`);

            // Determine the set type (don't mutate the original set)
            let setType = set.type;
            if (set.type === "RPE" && set.weight !== null) {
              setType = "WEIGHT";
            }

            // If the user went down/up in weight mid-exercise, carry their
            // final weight into next week's output sets.
            if (trendWeight !== null && weight !== null) {
              adjustedSets[set.originalIndex] = {
                ...set,
                type: setType,
                weight: trendWeight,
                rpe: null,
                video: null, // Remove video from new workout sets
                completed: false, // New workout sets should not be completed
              };
              return;
            }

            // If no weight, handle RPE logic
            if (!weight) {
              if (rpe) {
                // If we have a standardized weight and avgRPE for this group, use it for adjustment
                if (standardizedWeight !== null && avgRPE !== null) {
                  const newWeight = calculateWeightAdjustment(
                    standardizedWeight,
                    avgRPE,
                    weightUnit,
                    isAccessory
                  );
                  console.log(
                    `      No individual weight, using standardized weight ${standardizedWeight} for RPE adjustment: ${newWeight}`
                  );
                  adjustedSets[set.originalIndex] = {
                    ...set,
                    type: setType,
                    weight: newWeight,
                    rpe: null,
                    video: null, // Remove video from new workout sets
                    completed: false, // New workout sets should not be completed
                  };
                } else {
                  // Keep existing RPE if there is one and no standardized weight
                  console.log(`No weight, keeping RPE: ${rpe}`);
                  adjustedSets[set.originalIndex] = {
                    ...set,
                    type: setType,
                    rpe,
                    video: null, // Remove video from new workout sets
                    completed: false, // New workout sets should not be completed
                  };
                }
              } else {
                // Set RPE to 8 if neither weight nor RPE exists
                console.log(`No weight or RPE, setting RPE to 8`);
                adjustedSets[set.originalIndex] = {
                  ...set,
                  type: setType,
                  rpe: 8,
                  video: null, // Remove video from new workout sets
                  completed: false, // New workout sets should not be completed
                };
              }
              return;
            }

            // If weight exists but no RPE, use standardized weight if available
            if (!rpe) {
              const finalWeight =
                standardizedWeight !== null ? standardizedWeight : weight;
              console.log(
                `      Has weight (${weight}) but no RPE, using ${
                  standardizedWeight !== null ? "standardized" : "individual"
                } weight: ${finalWeight}`
              );
              adjustedSets[set.originalIndex] = {
                ...set,
                type: setType,
                weight: finalWeight,
                video: null, // Remove video from new workout sets
                completed: false, // New workout sets should not be completed
              };
              return;
            }

            // If both weight and RPE exist, adjust weight based on group avgRPE and clear RPE
            if (avgRPE !== null) {
              const baseWeight =
                standardizedWeight !== null ? standardizedWeight : weight;
              const newWeight = calculateWeightAdjustment(
                baseWeight,
                avgRPE,
                weightUnit,
                isAccessory
              );
              console.log(
                `      Has weight (${weight} ${weightUnit}) and RPE (${rpe}), adjusting based on group avgRPE ${avgRPE}: ${baseWeight} -> ${newWeight}`
              );
              adjustedSets[set.originalIndex] = {
                ...set,
                type: setType,
                weight: newWeight,
                rpe: null,
                video: null, // Remove video from new workout sets
                completed: false, // New workout sets should not be completed
              };
            } else {
              // No avgRPE for this group, keep original weight
              console.log(
                `      No avgRPE for this group, keeping original weight: ${weight}`
              );
              adjustedSets[set.originalIndex] = {
                ...set,
                type: setType,
                weight: weight,
                rpe: null,
                video: null, // Remove video from new workout sets
                completed: false, // New workout sets should not be completed
              };
            }
          });
        });

        // For circuit exercises, always clear RPE on output sets
        if (exerciseType === "circuit") {
          return adjustedSets.map((s) => (s ? { ...s, rpe: null } : s));
        }
        return adjustedSets;
      };

      const adjustedExercises = originalExercises.map((exercise) => {
        console.log(`Processing exercise: ${exercise.name}`);
        console.log(`  Exercise type: ${exercise.exerciseType}`);
        console.log(`  Original sets:`, exercise.sets);

        // Handle circuit exercises differently
        if (exercise.exerciseType === "circuit") {
          console.log(
            `  Processing circuit exercise with ${exercise.rounds || 1} rounds`
          );
          console.log(`  SubExercises:`, exercise.subExercises);
          console.log(`  Circuit:`, exercise.circuit);

          const processCircuitExerciseSets = (sets, weightUnit, fields) =>
            processSets(sets, weightUnit, "circuit", fields);

          // Process each subExercise
          const adjustedSubExercises = (exercise.subExercises || []).map(
            (subExercise) => {
              console.log(`  Processing subExercise: ${subExercise.name}`);
              console.log(`    Original sets:`, subExercise.sets);

              const weightUnit =
                subExercise.weightUnit || exercise.weightUnit || "lbs";
              const adjustedSets = processCircuitExerciseSets(
                subExercise.sets,
                weightUnit,
                subExercise.fields
              );

              return {
                ...subExercise,
                sets: adjustedSets,
              };
            }
          );

          // Process circuit array if present (primary structure for per-round data)
          let adjustedCircuit;
          if (
            exercise.circuit &&
            Array.isArray(exercise.circuit) &&
            exercise.circuit.length > 0
          ) {
            adjustedCircuit = exercise.circuit.map((round) =>
              round.map((subEx) => {
                const weightUnit =
                  subEx.weightUnit || exercise.weightUnit || "lbs";
                const adjustedSets = processCircuitExerciseSets(
                  subEx.sets || [],
                  weightUnit,
                  subEx.fields
                );
                return {
                  ...subEx,
                  sets: adjustedSets,
                };
              })
            );
          }

          const result = {
            ...exercise,
            rounds: exercise.rounds || 1,
            subExercises: adjustedSubExercises,
            sets: [],
            comments: [],
          };
          if (adjustedCircuit) {
            result.circuit = adjustedCircuit;
          }
          return result;
        }

        // Handle regular exercises
        const weightUnit = exercise.weightUnit || "lbs";
        const adjustedSets = processSets(
          exercise.sets,
          weightUnit,
          exercise.exerciseType,
          exercise.fields
        );

        return {
          ...exercise,
          sets: adjustedSets,
          comments: [], // Remove comments from new workout exercises
        };
      });

      console.log("Final adjusted exercises:", adjustedExercises);

      const newWorkout = {
        name: reviewingActivity.name,
        description: reviewingActivity.description,
        currentWeek: nextWeek,
        programLength: reviewingActivity.programLength,
        date: nextWeekDate, // Keep as moment object for AddWorkoutDetails
        exercises: adjustedExercises,
        completed: false,
        reviewed: false,
      };

      console.log("New workout to be created:", newWorkout);

      // Set the new workout data to show side by side
      setNewWorkoutData(newWorkout);
    } catch (error) {
      console.error("Error in auto-review:", error);
      showSnackbar("Failed to auto-review workout.", "error");
    } finally {
      setAutoReviewLoading(false);
    }
  };

  const handleActivityUpdate = () => {
    onReviewed();
  };

  const handleCloseNewWorkout = () => {
    setShowAutoReviewedWorkout(false);
  };

  const handleSaveNewWorkout = async (workoutData) => {
    try {
      // AddWorkoutDetails already converts the date to unix timestamp
      // So we can use the payload directly
      await addWorkoutForUser(reviewingActivity.userId, workoutData);

      // Now mark the original workout as reviewed
      await saveWorkoutDetails(reviewingActivity.id, { reviewed: true });
      setReviewed(true);

      showSnackbar("Workout copied to next week and reviewed.", "success");
      handleCloseNewWorkout();

      // Update local state to hide the reviewed workout instead of refetching
      if (onWorkoutReviewed) {
        onWorkoutReviewed(reviewingActivity.id);
      } else {
        // Fallback to refetching if callback not provided
        onReviewed();
      }
    } catch (error) {
      console.error("Error saving new workout:", error);
      showSnackbar("Failed to save new workout.", "error");
    }
  };

  return (
    <Grid2>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          alignItems: "stretch",
          width: "100%",
          maxWidth: "1600px",
          mx: "auto",
        }}
      >
        {/* Old Workout */}
        <Box sx={{ flex: newWorkoutData ? 1 : "1 1 100%", minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
              mt: 0,
            }}
          ></Box>
          <Box
            sx={{
              pv: 2,
              backgroundColor: "white",
              minHeight: "100%",
            }}
          >
            <Form
              onSubmit={(values) =>
                handleSaveNewWorkout(prepareNextWeekPayload(values))
              }
              initialValues={nextWeekInitialValues}
              mutators={{ ...arrayMutators }}
              subscription={{
                submitting: true,
                pristine: true,
                values: true,
              }}
              render={({ handleSubmit, form }) => (
                <>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 0.5 }}>
                            {activity.name}
                          </Typography>
                          <Typography sx={{ mb: 0.5 }}>
                            {activity.user.name}
                          </Typography>
                          <Typography sx={{ mb: 1.5 }}>
                            {moment.unix(activity.date).format("MMMM Do YYYY")}
                          </Typography>
                          <Typography sx={{ mb: 0.5 }} color="text.secondary">
                            Week {activity.currentWeek ?? "—"} of{" "}
                            {activity.programLength ?? "—"}
                          </Typography>
                          {activity.description && (
                            <Typography sx={{ mb: 1.5 }} color="text.secondary">
                              {activity.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexDirection: "column",
                          justifyContent: "space-between",
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            flex: 1,
                            justifyContent: "flex-end",
                            gap: 2,
                            mb: 4,
                          }}
                        >
                          {!hideReviewButton && (
                            <>
                              <LoadingButton
                                onClick={handleShowAutoReview}
                                variant="contained"
                                loading={autoReviewLoading}
                                color="success"
                                startIcon={<AutoAwesomeIcon />}
                                sx={{ mr: 1 }}
                              >
                                Auto Review
                              </LoadingButton>
                              <LoadingButton
                                onClick={toggleReviewed}
                                variant="contained"
                                loading={loading || activityLoading}
                                color={reviewed ? "info" : "primary"}
                              >
                                {reviewed ? "Reviewed" : "Mark Reviewed"}
                              </LoadingButton>
                            </>
                          )}
                        </Box>
                        {showAutoReviewedWorkout && (
                          <Box>
                            <Box
                              component="form"
                              onSubmit={handleSubmit}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 2,
                                  flex: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Typography variant="h6">
                                    New Workout
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      onClick={handleSubmit}
                                      startIcon={<SendIcon />}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      onClick={handleCloseNewWorkout}
                                      startIcon={<CancelIcon />}
                                    >
                                      Close
                                    </Button>
                                  </Box>
                                </Box>
                                <Box>
                                  <WorkoutMetaFields form={form} />
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <FieldArray
                      name="exercises"
                      subscription={{ value: true }}
                      render={({ fields }) => (
                        <>
                          {fields.map((name, index) => {
                            const previousExercise =
                              activity?.exercises?.[index];
                            return (
                              <Box
                                key={name}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  mb: index === fields.length - 1 ? 0 : 8,
                                  alignItems: "anchor-center",
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: 1,
                                  }}
                                >
                                  {previousExercise && (
                                    <ReviewExercise
                                      workout={activity}
                                      exercise={previousExercise}
                                      index={index}
                                      workoutId={activity.id}
                                      onUpdate={handleActivityUpdate}
                                    />
                                  )}
                                </Box>
                                {showAutoReviewedWorkout && (
                                  <Card
                                    sx={{ flex: 1, minWidth: 0, p: 2 }}
                                    elevation={2}
                                  >
                                    <AddExercise
                                      index={index}
                                      removeExercise={() =>
                                        fields.remove(index)
                                      }
                                      inline
                                      onOpenVideoModal={handleOpenVideoModal}
                                      onMoveUp={() =>
                                        fields.move(index, index - 1)
                                      }
                                      onMoveDown={() =>
                                        fields.move(index, index + 1)
                                      }
                                      canMoveUp={index > 0}
                                      canMoveDown={index < fields.length - 1}
                                    />
                                  </Card>
                                )}
                              </Box>
                            );
                          })}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              flex: 1,
                            }}
                          >
                            {showAutoReviewedWorkout && (
                              <>
                                <Box sx={{ flex: 1 }} />
                                <Box
                                  sx={{
                                    flex: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Button
                                    size="small"
                                    sx={{ mt: 1 }}
                                    onClick={() => {
                                      const exerciseIndex = fields.length;
                                      fields.push({
                                        sets: [
                                          {
                                            reps: "",
                                            weight: "",
                                            rpe: "",
                                            time: "",
                                            weightPerSide: false,
                                            repsPerSide: false,
                                            backoffPercent: "",
                                            backoffFromIndex: null,
                                          },
                                        ],
                                        weightUnit: "lbs",
                                        exerciseType:
                                          getDefaultExerciseType(exerciseIndex),
                                        fields: ["REPS", "RPE", "WEIGHT"],
                                      });
                                    }}
                                  >
                                    Add Exercise
                                  </Button>
                                </Box>
                              </>
                            )}
                          </Box>
                        </>
                      )}
                    />
                  </Box>

                  <VideoSelectionModal
                    open={videoModalOpen}
                    onClose={handleCloseVideoModal}
                    onSelectVideo={(videoUrl) =>
                      handleSelectVideo(form, videoUrl)
                    }
                  />
                </>
              )}
            />
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid2>
  );
};

export const ReviewExercise = ({
  exercise: initialExercise,
  index,
  workoutId,
}) => {
  const EMPTY_EXERCISE = useMemo(
    () => ({ name: "New exercise", sets: [], comments: [] }),
    []
  );
  const { id, name } = useUserInfo();
  const { addComment, updateComment, deleteComment } = useApi();
  const [comment, setComment] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exercise, setExercise] = useState(initialExercise || EMPTY_EXERCISE);
  const [editingComment, setEditingComment] = useState(null);
  const [isDictating, setIsDictating] = useState(false);
  const [dictationBase, setDictationBase] = useState("");
  const [userEditedDuringDictation, setUserEditedDuringDictation] =
    useState(false);
  const commands = [
    {
      command: "save comment",
      callback: () => {
        stopDictation() && handleAddComment();
      },
    },
    {
      command: "period",
      callback: () => {
        setComment(comment.replace(" period", "."));
      },
    },
  ];
  const {
    transcript,
    listening,
    browserSupportsSpeechRecognition,
    resetTranscript,
  } = useSpeechRecognition({ commands, continuous: true });

  useEffect(() => {
    setExercise(initialExercise || EMPTY_EXERCISE);
    setSelectedVideo(null);
    setEditingComment(null);
    setComment("");
    setDictationBase("");
    setUserEditedDuringDictation(false);
  }, [initialExercise, EMPTY_EXERCISE]);

  useEffect(() => {
    if (
      !browserSupportsSpeechRecognition ||
      !isDictating ||
      userEditedDuringDictation
    )
      return;

    const base = dictationBase.trim();
    const spoken = (transcript || "").trim();
    const combined =
      base && spoken ? `${base} ${spoken}` : base ? base : spoken;

    setComment(combined);
  }, [
    transcript,
    listening,
    browserSupportsSpeechRecognition,
    isDictating,
    dictationBase,
    userEditedDuringDictation,
  ]);

  useEffect(() => {
    return () => {
      SpeechRecognition.abortListening();
      setIsDictating(false);
    };
  }, []);

  const startDictation = async () => {
    if (!browserSupportsSpeechRecognition || submitting) return;
    setDictationBase(comment);
    setUserEditedDuringDictation(false);
    resetTranscript();
    try {
      await SpeechRecognition.startListening({
        continuous: true,
        language: "en-US",
      });
      setIsDictating(true);
    } catch (err) {
      console.error("Error starting speech recognition", err);
      setIsDictating(false);
    }
  };

  const stopDictation = async () => {
    if (!isDictating) return;
    try {
      await SpeechRecognition.abortListening();
    } catch (err) {
      console.error("Error stopping speech recognition", err);
    } finally {
      resetTranscript();
      setIsDictating(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim() || submitting) return;
    setSubmitting(true);
    stopDictation();

    try {
      if (editingComment) {
        // Update existing comment
        await handleEditComment(editingComment);
      } else {
        // Add new comment
        const newComment = {
          userId: id,
          userName: name || "Coach",
          userType: "coach",
          message: comment.trim(),
          timestamp: Math.floor(Date.now() / 1000),
        };

        // Save to backend using new API
        const result = await addComment(workoutId, index, newComment);

        // Only update UI after successful API call
        if (result) {
          setExercise((prevExercise) => ({
            ...prevExercise,
            comments: [...(prevExercise.comments || []), newComment],
          }));
          setComment("");
          setUserEditedDuringDictation(false);
          setDictationBase("");
        } else {
          // Show error to user if comment wasn't saved
          console.error("Failed to save comment");
        }
      }
    } catch (error) {
      console.error("Error saving comment:", error);
      alert("Failed to save comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentToEdit) => {
    if (!comment.trim() || submitting) return;
    setSubmitting(true);

    try {
      const updatedComment = {
        ...commentToEdit,
        message: comment.trim(),
      };

      const result = await updateComment(workoutId, index, updatedComment);

      if (result) {
        setExercise((prevExercise) => ({
          ...prevExercise,
          comments: prevExercise.comments.map((c) =>
            c.timestamp === commentToEdit.timestamp ? updatedComment : c
          ),
        }));
        setComment("");
        setDictationBase("");
        setEditingComment(null);
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      alert("Failed to update comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentToDelete) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const result = await deleteComment(
        workoutId,
        index,
        commentToDelete.timestamp
      );

      if (result) {
        setExercise((prevExercise) => ({
          ...prevExercise,
          comments: prevExercise.comments.filter(
            (c) => c.timestamp !== commentToDelete.timestamp
          ),
        }));
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      alert("Failed to delete comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setComment("");
    setDictationBase("");
    setUserEditedDuringDictation(false);
  };

  const isCircuit = exercise.exerciseType === "circuit";

  return (
    <Card elevation={2} sx={{ p: 2 }}>
      {index !== undefined && <Typography>Exercise #{index + 1}</Typography>}
      <Typography variant="h6">{exercise.name}</Typography>
      {isCircuit && exercise.rounds && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Rounds: {exercise.rounds}
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        {selectedVideo && (
          <VideoPlayerWith1080pFallback videoKey={selectedVideo} />
        )}
      </Box>
      {isCircuit ? (
        // Display circuit exercises with rounds
        <Box sx={{ mt: 2 }}>
          {(() => {
            // Use circuit array if available, otherwise migrate from legacy subExercises
            let circuitRounds = exercise.circuit;
            if (
              !circuitRounds ||
              !Array.isArray(circuitRounds) ||
              circuitRounds.length === 0
            ) {
              if (exercise.subExercises && exercise.subExercises.length > 0) {
                const rounds = exercise.rounds || 1;
                circuitRounds = Array(rounds)
                  .fill(null)
                  .map(() => [...exercise.subExercises]);
              } else {
                return (
                  <Typography color="text.secondary">
                    No circuit exercises defined.
                  </Typography>
                );
              }
            }

            return circuitRounds.map((round, roundIndex) => (
              <Box key={`round-${roundIndex}`} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  Round {roundIndex + 1}
                </Typography>
                {round.map((subExercise, subIndex) => (
                  <Box key={`${roundIndex}-${subIndex}`} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      {subExercise.name}
                    </Typography>
                    {subExercise.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {subExercise.description}
                      </Typography>
                    )}
                    <SetsTable
                      sets={subExercise.sets || []}
                      setSelectedVideo={setSelectedVideo}
                      fields={subExercise.fields}
                    />
                  </Box>
                ))}
              </Box>
            ));
          })()}
        </Box>
      ) : (
        // Display regular exercise sets
        <SetsTable
          sets={exercise.sets || []}
          setSelectedVideo={setSelectedVideo}
          fields={exercise.fields}
        />
      )}

      {/* Comments Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Comments
        </Typography>

        {exercise.comments && exercise.comments.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {exercise.comments.map((c) => (
              <Card key={c.timestamp} variant="outlined" sx={{ mb: 1, p: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mr: 1 }}
                      >
                        {c.userName}
                      </Typography>
                      <Chip
                        label={c.userType}
                        size="small"
                        color={c.userType === "coach" ? "primary" : "success"}
                      />
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {c.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {moment.unix(c.timestamp).format("MMM D, YYYY h:mm A")}
                    </Typography>
                  </Box>
                  {c.userId === id && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setComment(c.message);
                          setDictationBase(c.message);
                          setEditingComment(c);
                        }}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteComment(c)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Card>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No comments yet
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={8}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (isDictating && !userEditedDuringDictation) {
                setUserEditedDuringDictation(true);
              }
            }}
            placeholder={
              editingComment ? "Edit comment..." : "Add a comment..."
            }
            slotProps={{
              htmlInput: {
                // Enable browser/native dictation controls where supported (e.g., Chrome)
                "x-webkit-speech": "true",
                "aria-label": editingComment
                  ? "Edit comment with dictation support"
                  : "Add a comment with dictation support",
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label={
                    isDictating ? "Stop dictation" : "Start dictation"
                  }
                  onClick={isDictating ? stopDictation : startDictation}
                  color={isDictating ? "primary" : "default"}
                  disabled={!browserSupportsSpeechRecognition || submitting}
                  edge="end"
                  size="small"
                >
                  {isDictating ? <StopCircleIcon /> : <KeyboardVoiceIcon />}
                </IconButton>
              ),
            }}
            variant="outlined"
          />
          {!browserSupportsSpeechRecognition && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              Speech recognition is not supported in this browser.
            </Typography>
          )}
          {isDictating && browserSupportsSpeechRecognition && (
            <Typography
              variant="caption"
              color="primary"
              sx={{ display: "block", mt: 0.5 }}
            >
              Listening...
            </Typography>
          )}
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 1, gap: 1 }}>
            {editingComment && (
              <Button
                onClick={handleCancelEdit}
                variant="outlined"
                color="secondary"
              >
                Cancel
              </Button>
            )}
            <LoadingButton
              onClick={handleAddComment}
              disabled={!comment.trim() || submitting}
              loading={submitting}
              variant="contained"
              endIcon={editingComment ? <EditIcon /> : <SendIcon />}
            >
              {submitting
                ? editingComment
                  ? "Updating..."
                  : "Sending..."
                : editingComment
                ? "Update Comment"
                : "Add Comment"}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const VIDEO_BASE_URL =
  "https://trainer-app-videos.s3.us-east-2.amazonaws.com";

const get1080pVideoKey = (key) => {
  if (!key || typeof key !== "string") return null;
  return key.replace(/(\.[^.]+)$/, "_1080p$1");
};

const VideoPlayerWith1080pFallback = ({ videoKey }) => {
  const [src, setSrc] = useState(() => {
    const key1080 = get1080pVideoKey(videoKey);
    return key1080 ? `${VIDEO_BASE_URL}/${key1080}` : `${VIDEO_BASE_URL}/${videoKey}`;
  });
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    const key1080 = get1080pVideoKey(videoKey);
    if (key1080) {
      setSrc(`${VIDEO_BASE_URL}/${key1080}`);
      setUseFallback(false);
    } else {
      setSrc(`${VIDEO_BASE_URL}/${videoKey}`);
      setUseFallback(true);
    }
  }, [videoKey]);

  const handleError = useCallback(() => {
    if (!useFallback && videoKey) {
      setSrc(`${VIDEO_BASE_URL}/${videoKey}`);
      setUseFallback(true);
    }
  }, [useFallback, videoKey]);

  if (!videoKey) return null;

  return (
    <CardMedia
      id="player"
      key={videoKey}
      fullWidth
      height="600"
      component="video"
      src={src}
      onError={handleError}
      controls
      autoPlay
    />
  );
};

const SetsTable = ({ sets, setSelectedVideo, fields }) => {
  // Start from the configured fields (if any), but always show any fields
  // that actually have data in the logged sets (TIME, RPE, etc.).
  const baseFields = Array.isArray(fields)
    ? fields.map((f) => f.toUpperCase())
    : (() => {
        const firstSet = sets?.[0] || {};
        if (
          firstSet.type != null ||
          (Array.isArray(firstSet.types) && firstSet.types.length > 0)
        ) {
          if (Array.isArray(firstSet.types) && firstSet.types.length > 0)
            return firstSet.types.map((t) => t.toUpperCase());
          if (firstSet.type === "TIME") return ["TIME"];
          if (firstSet.type === "RPE") return ["RPE", "WEIGHT"];
          return ["WEIGHT", "RPE"];
        }
        return ["RPE", "WEIGHT"];
      })();

  const fieldSet = new Set(baseFields);

  if (sets?.some((s) => s.weight != null)) fieldSet.add("WEIGHT");
  if (sets?.some((s) => s.rpe != null)) fieldSet.add("RPE");
  if (sets?.some((s) => s.reps != null && s.reps !== "")) fieldSet.add("REPS");
  if (sets?.some((s) => s.time != null)) fieldSet.add("TIME");
  if (sets?.some((s) => s.distance != null)) fieldSet.add("DISTANCE");
  if (sets?.some((s) => s.degree != null)) fieldSet.add("DEGREE");

  const selectedFields = Array.from(fieldSet);

  return (
    <Table sx={{ mb: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell>#</TableCell>
          {selectedFields.includes("REPS") && <TableCell align="center">Reps</TableCell>}
          {selectedFields.includes("WEIGHT") && <TableCell>Weight</TableCell>}
          {selectedFields.includes("RPE") && <TableCell>RPE</TableCell>}
          {selectedFields.includes("TIME") && <TableCell>Time (sec)</TableCell>}
          {selectedFields.includes("DISTANCE") && <TableCell>Distance</TableCell>}
          {selectedFields.includes("DEGREE") && <TableCell>Degree</TableCell>}
          <TableCell>Video</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sets.map((set, setIndex) => (
            <TableRow key={setIndex}>
              <TableCell>
                <Typography>{setIndex + 1}</Typography>
              </TableCell>
              {selectedFields.includes("REPS") && (
                <TableCell align="center">
                  <Typography>{set.reps ?? "—"}</Typography>
                </TableCell>
              )}
              {selectedFields.includes("WEIGHT") && (
                <TableCell>{set.weight ?? "—"}</TableCell>
              )}
              {selectedFields.includes("RPE") && (
                <TableCell>{set.rpe ?? "—"}</TableCell>
              )}
              {selectedFields.includes("TIME") && (
                <TableCell>{set.time ?? "—"}</TableCell>
              )}
              {selectedFields.includes("DISTANCE") && (
                <TableCell>{set.distance ?? "—"}</TableCell>
              )}
              {selectedFields.includes("DEGREE") && (
                <TableCell>{set.degree ?? "—"}</TableCell>
              )}
              <TableCell>
                {set.video ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        mt: 2,
                        mr: 1,
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedVideo(set.video)}
                    >
                      <PlayArrowIcon sx={{ position: "absolute" }} />
                    </Avatar>
                  </Box>
                ) : (
                  <CancelIcon sx={{ ml: 1 }} />
                )}
              </TableCell>
            </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ReviewClientActivity;
